import {
    createContext,
    type ReactNode,
    type FC,
    useEffect,
    useState,
    useContext,
    useCallback,
} from 'react';
import { trackEvent, trackEcommercePurchase } from 'dibs-tracking';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { useFragment, graphql, useRelayEnvironment, useRefetchableFragment } from 'react-relay';
import { utils, BigNumber } from 'ethers';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { useConnectedBalance } from 'dibs-crypto-utils/exports/useConnectedBalance';
import { getRevertReason } from 'dibs-crypto-utils/exports/getRevertReason';
import { useEtherConversion } from 'dibs-crypto-utils/exports/useEtherConversion';
import {
    type GetConvertedEther,
    type GetConvertedEtherUnformatted,
    type GetAmountStrings,
} from 'dibs-crypto-utils/exports/useEtherConversion';
import { type ZipCodeOutput } from 'dibs-crypto-utils/exports/EthereumConnectionZipCodeForm';
import { BID, BID_PENDING, BID_SUCCESSFUL, LOADING, ZIP_CODE } from './bidModalTypes';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { useLatestAuctionInfo } from './hooks/useLatestAuctionInfo';
import { useLatestUserInfo } from './hooks/useLatestUserInfo';
import { NftPdpContractContext } from '../NftPdpContractContext/NftPdpContractContext';
import { type BidModalType } from './bidModalTypes';

import { type NftPdpBidModalContextQuery as NftPdpBidModalContextQueryType } from './__generated__/NftPdpBidModalContextQuery.graphql';
import { type NftPdpBidModalContext_nftItem$key } from './__generated__/NftPdpBidModalContext_nftItem.graphql';
import { type NftPdpBidModalContext_viewer$key } from './__generated__/NftPdpBidModalContext_viewer.graphql';

const messages = defineMessages({
    genericTransactionFailed: {
        id: 'nft.pdp.bidModal.genericTransactionFailed',
        defaultMessage: 'Your bid could not be processed. Please try again.',
    },
    insufficientFunds: {
        id: 'nft.pdp.bidModal.insufficientFunds',
        defaultMessage:
            'You do not have enough funds in your wallet to place this bid. Please add more funds to your wallet via MetaMask in order to continue.',
    },
    minBidNotMet: {
        id: 'nft.pdp.bidModal.minBidNotMet',
        defaultMessage:
            'Your bid was rejected. Please try submitting again with a bid bigher than the minimum bid.',
    },
    outbid: {
        id: 'nft.pdp.bidModal.outbid',
        defaultMessage:
            'You were outbid. Please try submitting again with a bid higher than the minimum bid.',
    },
    globallyPaused: {
        id: 'nft.pdp.bidModal.globalPaused',
        defaultMessage: 'Execution reverted: Auctions are globally paused',
    },
});

type NftPdpBidModalContextProps = {
    children: ReactNode;
    nftItem: NftPdpBidModalContext_nftItem$key;
    viewer: NftPdpBidModalContext_viewer$key | null | undefined;
};

// TODO: move to auth modal in DBL
type AuthResponse = {
    user: {
        serviceId: string;
        ethereumWalletAddress: {
            address: string;
        };
    };
};

type NftPdpBidModalContextType = {
    modalType: BidModalType | null;
    setModalTypeTrackingLabel: (modalType: string) => void;
    pendingCurrentBid: string | null;
    pendingTotalAmount: string | null;
    placeBid: (arg0: { bidAmount: string; totalAmount: string }) => void;
    connectToMetamaskForBidderRole: (zipCodeOutput: ZipCodeOutput) => Promise<void>;
    bidError: string | null;
    onAuthComplete: (res: AuthResponse) => void;
    getConvertedEther: GetConvertedEther;
    getConvertedEtherUnformatted: GetConvertedEtherUnformatted;
    getAmountStrings: GetAmountStrings;
    transactionHash: string | null;
    setTransactionHash: (hash: string | null) => void;
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    updateModalType: () => void;
    openBidModal: () => void;
    persistedAddress?: string;
    closeModal: () => void;
    walletBalance: string | null;
    formattedWalletBalance: string | null;
    isBidInFlight: boolean;
    currentBidWei?: string | null;
    auctionStatus?: string | null;
    auctionEndDate?: string | null;
    isHighestBidder: boolean;
    isOwner: boolean;
    isSeller: boolean;
    isAwaitingSettlement: boolean;
    hasMismatchedAddress: boolean;
    metamaskConnectTrackingLabel: string;
};

export const NftPdpBidModalContext = createContext<NftPdpBidModalContextType>({
    modalType: null,
    setModalTypeTrackingLabel: () => {},
    pendingCurrentBid: null,
    pendingTotalAmount: null,
    placeBid: () => {},
    bidError: null,
    connectToMetamaskForBidderRole: () => Promise.resolve(),
    onAuthComplete: () => {},
    getConvertedEther: () => '',
    getConvertedEtherUnformatted: () => '',
    getAmountStrings: () => ({
        ether: '',
        etherNoSymbol: '',
        converted: '',
        etherWithConversion: '',
    }),
    transactionHash: '',
    setTransactionHash: () => {},
    modalOpen: false,
    setModalOpen: () => {},
    updateModalType: () => {},
    openBidModal: () => {},
    persistedAddress: '',
    closeModal: () => {},
    walletBalance: null,
    formattedWalletBalance: null,
    isBidInFlight: false,
    currentBidWei: null,
    auctionStatus: null,
    auctionEndDate: null,
    isHighestBidder: false,
    isOwner: false,
    isSeller: false,
    isAwaitingSettlement: false,
    hasMismatchedAddress: false,
    metamaskConnectTrackingLabel: '',
});

function trackBidError(label: string): void {
    trackEvent({
        event: 'e_ga_event',
        category: 'NFT',
        action: 'ethereum error displayed',
        label,
    });
}

function trackBidExit(label: BidModalType | string | null): void {
    trackEvent({
        event: 'e_ga_event',
        category: 'NFT',
        action: 'NFT bid Exit',
        label,
    });
}

export const NftPdpBidModalContextProvider: FC<NftPdpBidModalContextProps> = ({
    children,
    nftItem: nftItemRef,
    viewer: viewerRef,
}) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const { currency } = useCurrency();
    const {
        account: connectedAddress,
        initiateConnection,
        connected,
        alchemyProvider,
        chainId,
    } = useWeb3Connection();
    const { auctionContract } = useContext(NftPdpContractContext);
    const { fetchBalance, balance, formattedBalance, balanceBigNumber } = useConnectedBalance();
    const nftItem = useFragment(
        graphql`
            fragment NftPdpBidModalContext_nftItem on NftItem {
                ...useLatestUserInfo_nftItem
                serviceId
                tokenId
                contract {
                    address
                }
                mostRecentAuction {
                    ...useLatestAuctionInfo_nftAuction
                    auctionId
                    endedConversionRates {
                        rates {
                            ...useEtherConversion_etherRates
                        }
                    }
                }
                balanceForUser(userId: $userId) @include(if: $fetchUser) {
                    balance
                    ownerAddress {
                        address
                    }
                }
                etherRates {
                    ...useEtherConversion_etherRates
                }
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                    }
                }
                creatorAddress {
                    publicProfile {
                        profileName
                    }
                }
                edition
            }
        `,
        nftItemRef
    );
    const [viewer, refetchViewer] = useRefetchableFragment<
        NftPdpBidModalContextQueryType,
        NftPdpBidModalContext_viewer$key
    >(
        graphql`
            fragment NftPdpBidModalContext_viewer on Viewer
            @refetchable(queryName: "NftPdpBidModalContextQuery") {
                ...useLatestUserInfo_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    socialNetworkByName(socialNetworkName: ETHEREUM) {
                        ... on EthereumSocialConnection {
                            userPreferences {
                                zipCode
                            }
                        }
                    }
                }
            }
        `,
        viewerRef
    );

    const {
        mostRecentAuction,
        etherRates,
        metadata,
        creatorAddress,
        edition,
        tokenId,
        contract,
        serviceId: itemId,
        balanceForUser,
    } = nftItem || {};
    const { address } = contract || {};
    const tokenTrackingString = `${address}/id/${tokenId}`;
    const { auctionId, endedConversionRates } = mostRecentAuction || {};

    const [fetchingZipCode, setFetchingZipCode] = useState(false);
    const [hasZipCode, setHasZipCode] = useState(
        !!viewer?.user?.socialNetworkByName?.userPreferences?.zipCode
    );

    const {
        fetchLatestAuctionInfo,
        currentBidWei,
        auctionStatus,
        auctionEndDate,
        currentAuctionWinnerAddress,
        isAwaitingSettlement,
    } = useLatestAuctionInfo({
        nftAuction: mostRecentAuction,
    });
    const { isHighestBidder, isOwner, isSeller, hasMismatchedAddress, userId, persistedAddress } =
        useLatestUserInfo({
            nftItem,
            viewer,
            currentAuctionWinnerAddress,
        });
    const { getConvertedEther, getConvertedEtherUnformatted, getAmountStrings } =
        useEtherConversion({
            currency,
            etherRates: (endedConversionRates?.rates || etherRates)?.filter(filterNulls) || null,
        });

    const [modalType, setModalType] = useState<BidModalType | null>(null);
    const [modalTypeTrackingLabel, setModalTypeTrackingLabel] = useState<string>('');
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [pendingCurrentBid, setPendingCurrentBid] = useState<string | null>(null);
    const [pendingTotalAmount, setPendingTotalAmount] = useState<string | null>(null);
    const [bidError, setBidError] = useState<string | null>(null);
    const [transactionHash, setTransactionHash] = useState<string | null>(null);

    const changeModalType = (_modalType: BidModalType): void => {
        setModalType(_modalType);
        setModalTypeTrackingLabel(_modalType);
    };

    /**
     *  handle bid going from pending to submitted
     */
    const pendingBidConfirmed = pendingCurrentBid !== null && isHighestBidder;
    const [isBidInFlight, setIsBidInFlight] = useState(false);
    useEffect(() => {
        if (pendingBidConfirmed) {
            const convertedBidAmount = getConvertedEtherUnformatted(
                `${utils.formatEther(BigNumber.from(pendingCurrentBid))}`,
                'USD'
            );
            const convertedTotalAmount = getConvertedEtherUnformatted(
                `${utils.formatEther(BigNumber.from(pendingTotalAmount))}`,
                'USD'
            );
            trackEvent({
                event: 'e_ga_event',
                category: 'nft',
                action: 'nft bid complete',
                label: tokenTrackingString,
                value: convertedBidAmount,
                eventName: 'auction_bid',
                interaction_type: 'nft auction',
                step_interaction_name: 'complete',
                additional: {
                    auction_type: 'nft',
                },
            });
            trackEcommercePurchase({
                type: 'purchase',
                actionField: {
                    revenue: convertedTotalAmount,
                    id: transactionHash,
                },
                products: [
                    {
                        id: itemId,
                        itemId,
                        orderId: transactionHash,
                        name: metadata?.name,
                        orderType: 'NFT bid',
                        price: convertedTotalAmount,
                        priceType: 'ethereum',
                        brand: creatorAddress?.publicProfile?.profileName,
                        category: '/nft/art/',
                        quantity: edition,
                    },
                ],
            });
            setBidError(null);
            changeModalType(BID_SUCCESSFUL);
            setPendingCurrentBid(null);
            setPendingTotalAmount(null);
            setIsBidInFlight(false);
            fetchBalance();
        }
    }, [
        creatorAddress,
        edition,
        fetchBalance,
        getConvertedEtherUnformatted,
        itemId,
        metadata,
        pendingBidConfirmed,
        pendingCurrentBid,
        pendingTotalAmount,
        tokenTrackingString,
        transactionHash,
    ]);

    const returnToBidModal = useCallback(({ error }: { error?: string | null }): void => {
        if (typeof error !== 'undefined') {
            setBidError(error);
        }
        setIsBidInFlight(false);
        setPendingCurrentBid(null);
        setPendingTotalAmount(null);
        changeModalType(BID);
    }, []);

    const placeBid = useCallback(
        async ({ bidAmount, totalAmount }: { bidAmount: string; totalAmount: string }) => {
            const convertedBidAmount = getConvertedEtherUnformatted(`${bidAmount}`, 'USD');

            trackEvent({
                event: 'e_ga_event',
                category: 'NFT',
                action: 'NFT bid interaction - confirm bid',
                label: tokenTrackingString,
                value: convertedBidAmount,
            });
            const parsedTotalAmount = utils.parseEther(totalAmount);
            const parsedBidAmount = utils.parseEther(bidAmount);
            if (balanceBigNumber && parsedTotalAmount.gt(balanceBigNumber)) {
                setBidError(intl.formatMessage(messages.insufficientFunds));
                return;
            }
            if (auctionContract && auctionId && !isBidInFlight) {
                try {
                    setIsBidInFlight(true);
                    const transaction = await auctionContract.bid(
                        auctionId,
                        parsedTotalAmount.toString(),
                        {
                            value: parsedTotalAmount.toString(),
                        }
                    );
                    setPendingCurrentBid(parsedBidAmount.toString()); // persist loading state until latest bid is received from the subscription
                    setPendingTotalAmount(parsedTotalAmount.toString()); // persist loading state until latest bid is received from the subscription
                    changeModalType(BID_PENDING);
                    setTransactionHash(transaction.hash);
                    const receipt = await transaction.wait(); // wait for transaction to finish in case an error is thrown
                    setTransactionHash(receipt.transactionHash);
                    setBidError(null);
                } catch (e: $TSFixMe) {
                    if (e.receipt && alchemyProvider) {
                        // check failure reason if transaction fails after being submitted
                        let revertReason = '';
                        try {
                            // wait for transaction to get mined before getting reject reason
                            // to ensure that you get it from the correct block
                            await alchemyProvider.waitForTransaction(e.receipt.transactionHash);
                            revertReason = await getRevertReason(
                                alchemyProvider,
                                e.receipt.transactionHash,
                                e.receipt.blockNumber
                            );
                        } catch (err) {
                            // do nothing with this error
                        }
                        // get latest on-chain auction info before surfacing error in case subscription hasn't updated it yet
                        await fetchLatestAuctionInfo();
                        let errorMessage = intl.formatMessage(messages.genericTransactionFailed);
                        if (revertReason.includes('_validateAndGetBid')) {
                            errorMessage = intl.formatMessage(messages.outbid);
                        } else if (revertReason.includes('globally paused')) {
                            errorMessage = intl.formatMessage(messages.globallyPaused);
                        }
                        trackBidError(errorMessage);
                        returnToBidModal({
                            error: errorMessage,
                        });
                    } else if (e.code === 4001) {
                        // metamask closed by user
                        returnToBidModal({ error: null });
                    } else if (e.message.includes('_validateAndGetBid')) {
                        // this sometimes rejects before metamask even opens, and seems not to happen at the transaction level
                        // metamask rejects based on ABI?
                        const errorMessage = intl.formatMessage(messages.minBidNotMet);
                        trackBidError(errorMessage);
                        returnToBidModal({ error: errorMessage });
                    } else if (e.message.includes('globally paused')) {
                        const errorMessage = intl.formatMessage(messages.globallyPaused);
                        trackBidError(errorMessage);
                        returnToBidModal({ error: errorMessage });
                    } else {
                        const errorMessage = intl.formatMessage(messages.genericTransactionFailed);
                        trackBidError(errorMessage);
                        returnToBidModal({
                            error: errorMessage,
                        });
                    }
                }
            }
        },
        [
            alchemyProvider,
            auctionContract,
            auctionId,
            balanceBigNumber,
            fetchLatestAuctionInfo,
            getConvertedEtherUnformatted,
            intl,
            isBidInFlight,
            returnToBidModal,
            tokenTrackingString,
        ]
    );

    const connectToMetamaskForBidderRole = useCallback(
        ({
            zipCode,
            isInternationalCustomer,
        }: {
            zipCode: string | null;
            isInternationalCustomer: boolean;
        }) =>
            initiateConnection({
                environment,
                userId: userId || '',
                zipCode,
                isInternationalCustomer,
                onSign: () => {
                    setHasZipCode(true);
                    trackEvent({
                        event: 'e_ga_event',
                        category: 'nft',
                        action: 'metamask connect complete',
                        eventName: 'add_payment_info',
                        interaction_type: 'metamask wallet connect',
                        step_interaction_name: 'complete',
                        additional: {
                            auction_type: 'nft',
                        },
                    });
                },
            }),
        [environment, initiateConnection, setHasZipCode, userId]
    );

    /* used for calls form updateModalType to avoid circular deps modalType <-> updateModalType */
    const _closeModal = useCallback(
        (_modalType: string | null) => {
            setModalOpen(false);
            setBidError(null);
            if (![BID_PENDING, BID_SUCCESSFUL].includes(_modalType ?? '')) {
                if (_modalType === BID && bidError) {
                    trackBidExit(bidError);
                } else {
                    trackBidExit(_modalType);
                }
            }
        },
        [bidError]
    );

    const closeModal = useCallback(() => {
        _closeModal(modalTypeTrackingLabel);
    }, [modalTypeTrackingLabel, _closeModal]);

    /**
     * launch the bid modal from the CTA in the sidebar
     */
    const [completedAuthFlow, setCompletedAuthFlow] = useState(false);

    const updateModalType = useCallback(() => {
        const connectedAddressLowerCase = connectedAddress?.toLowerCase();

        if (connected.connecting) {
            return;
        }
        if (fetchingZipCode) {
            changeModalType(LOADING);
        } else if (persistedAddress && connectedAddressLowerCase && !hasZipCode) {
            //// bidder role assigned only when zipcode is provided - ask for zipcode
            changeModalType(ZIP_CODE);
        } else if (chainId && persistedAddress && connectedAddressLowerCase) {
            // correct chain, matching addresses, so you get the real bid modal
            changeModalType(BID);
        }
    }, [
        connected.connecting,
        connectedAddress,
        persistedAddress,
        chainId,
        hasZipCode,
        fetchingZipCode,
    ]);

    /*
     * whenever we have a new instance of updateModalType and modal is open
     * we should call updateModalType to react its dependency changes
     */
    useEffect(() => {
        if (modalOpen) {
            updateModalType();
        }
    }, [modalOpen, updateModalType]);

    const openBidModal = useCallback(() => {
        setModalOpen(true);
    }, []);

    const onAuthComplete = useCallback(
        async (authResponse: AuthResponse) => {
            const authPersistedAddress =
                authResponse?.user?.ethereumWalletAddress?.address?.toLowerCase();

            const owner =
                authPersistedAddress && balanceForUser?.balance && balanceForUser?.balance > 0;
            const highestBidder =
                authPersistedAddress &&
                currentAuctionWinnerAddress &&
                authPersistedAddress === currentAuctionWinnerAddress;

            if (owner || highestBidder) {
                _closeModal(modalType);
            } else {
                setFetchingZipCode(true);
                refetchViewer(
                    {},
                    { onComplete: () => setFetchingZipCode(false), fetchPolicy: 'network-only' }
                );
                setCompletedAuthFlow(true);
            }
        },
        [balanceForUser, currentAuctionWinnerAddress, _closeModal, modalType, refetchViewer]
    );

    const _setModalTypeTrackingLabel = (_modalTypeTrackingLabel: string): void => {
        // empty label means context consumers are not in control of the modal type - context is
        setModalTypeTrackingLabel(_modalTypeTrackingLabel || modalType || '');
    };

    const metamaskConnectTrackingLabel = completedAuthFlow
        ? 'login - NFT PDP'
        : 'bid click - NFT PDP';

    return (
        <NftPdpBidModalContext.Provider
            value={{
                modalType,
                setModalTypeTrackingLabel: _setModalTypeTrackingLabel,
                pendingTotalAmount,
                pendingCurrentBid,
                placeBid,
                bidError,
                connectToMetamaskForBidderRole,
                onAuthComplete,
                getConvertedEther,
                getConvertedEtherUnformatted,
                getAmountStrings,
                transactionHash,
                setTransactionHash,
                modalOpen,
                setModalOpen,
                updateModalType,
                openBidModal,
                persistedAddress,
                closeModal,
                walletBalance: balance,
                formattedWalletBalance: formattedBalance,
                isBidInFlight,
                currentBidWei,
                auctionStatus,
                auctionEndDate,
                isHighestBidder,
                isOwner,
                isSeller,
                isAwaitingSettlement,
                hasMismatchedAddress,
                metamaskConnectTrackingLabel,
            }}
        >
            {children}
        </NftPdpBidModalContext.Provider>
    );
};
